import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {Entity, EntityType} from "./entity";
import {ObjectPreferenceArrayCodec, ObjectPreferenceEntity} from "./object-preference";
import {WaterGoPreferenceEntity, WaterGoPreferenceCodec} from "./water-go-preference";
import {OilGoPreferenceEntity, OilGoPreferenceCodec} from "./oil-go-preference";
import {validateJSON, validateJSONNow} from "../web/validation";
import {RrcWasteType, toRrcWasteType} from "./rrc/rrc-waste-type";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";

/** Validator for freight Type JSON */
export const FreightTypeValidator = t.intersection([
  t.type({
    id: t.string,
    type: t.string,
    doNotUse: t.boolean
  }),
  t.partial({
    createDttm: t.number,
    lastModifiedDttm: t.number,
    description: t.string,
    cargoType: t.string,
    cargoSubType: t.string,
    unitOfMeasure: t.string,
    abbr: t.string,
    category: t.string,
    unNumber: t.number,
    shippingName: t.string,
    hazardClass: t.number,
    packingGroup: t.string,
    rrcWasteType: t.string,
    regionIds: t.array(t.string),

    objectPreferences: ObjectPreferenceArrayCodec,
    waterGoPreferences: WaterGoPreferenceCodec,
    oilGoPreference: OilGoPreferenceCodec,
  })
]);

/** Validator for an JSON array of Freight Types */
export const FreightTypeArrayValidator = t.array(FreightTypeValidator);

export type FreightType = t.TypeOf<typeof FreightTypeValidator>;
export type FreightTypeArray = t.TypeOf<typeof FreightTypeArrayValidator>;

/**
 * A freight type.
 */
@Exclude()
export class FreightTypeEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  private type!: string;

  @Expose()
  description?: string;

  @Expose()
  cargoType?: string;

  @Expose()
  cargoSubType?: string;

  @Expose()
  unitOfMeasure?: string;

  @Expose()
  abbr?: string;

  @Expose()
  category?: string;

  @Expose()
  unNumber?: number;

  @Expose()
  shippingName?: string;

  @Expose()
  hazardClass?: number;

  @Expose()
  packingGroup?: string;

  @Transform(getDatabaseCodeTransformer(toRrcWasteType))
  @Expose()
  rrcWasteType?: RrcWasteType

  @Expose()
  doNotUse = false;

  @Expose()
  regionIds?: string[];

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  @Type(() => ObjectPreferenceEntity)
  objectPreferences?: ObjectPreferenceEntity[]

  @Expose()
  @Type(() => WaterGoPreferenceEntity)
  waterGoPreference?: WaterGoPreferenceEntity

  @Expose()
  @Type(() => OilGoPreferenceEntity)
  oilGoPreferenceEntity?: OilGoPreferenceEntity

  readonly entityType: EntityType = "FREIGHTTYPE";

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for FreightTypeEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: FreightType): FreightTypeEntity {
    return plainToInstance(FreightTypeEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<FreightType> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(FreightTypeValidator, object);
  }

  asTypeValue(): FreightType {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(FreightTypeValidator, object);
  }

  get name(): string {
    return this.type;
  }

  set name(name: string) {
    this.type = name;
  }
}
